
import { useState, useEffect } from "react";
import { vendorArtistApi, publicApi, adminApi, getImageUrl } from "../../lib/api";
import { Panel, StatusBadge, EmptyState } from "./vendorShared";
// import specialistImage from "../../assets/spec1.png";
// import portfolioOne from "../../assets/IS1.png";
// import portfolioTwo from "../../assets/IS2.png";
// import portfolioThree from "../../assets/IS3.png";
import {
    getCategoryId, getSubcategoryId, normalizeService,
    normalizeBlog, normalizeSponsor, normalizeRentalStudio, normalizePageant
} from "../../utils/vendor/vendorUtils";

const isSuccessMessage = (msg) => {
    if (!msg) return false;
    const lower = msg.toLowerCase();
    return lower.includes("success") ||
        lower.includes("saved") ||
        lower.includes("updated") ||
        lower.includes("created") ||
        lower.includes("deleted") ||
        lower.includes("loaded") ||
        lower.includes("sent") ||
        lower.includes("verified");
};

export function ServicesPanel({ dashboardServices, dashboardCategories, dashboardSubcategories, onServiceCreated, onServiceUpdated, onServiceDeleted, onCategoryCreated, onCategoryUpdated, onCategoryDeleted, onSubcategoryCreated, onSubcategoryUpdated, onSubcategoryDeleted }) {
    console.log("Function called: ServicesPanel");
    const categoryMap = new Map(dashboardCategories.map((category) => [Number(getCategoryId(category)), category.title || category.name || `Category ${getCategoryId(category)}`]));
    const subcategoryMap = new Map(dashboardSubcategories.map((subcat) => [Number(getSubcategoryId(subcat)), subcat.title || subcat.name || `Subcategory ${getSubcategoryId(subcat)}`]));
    const serviceRows = dashboardServices.map((service) => normalizeService(service, categoryMap));
    const [showServiceForm, setShowServiceForm] = useState(false);
    const [editingService, setEditingService] = useState(null);
    const [serviceForm, setServiceForm] = useState({
        name: "",
        categoryid: "",
        subcatid: "",
        price: "",
        time: "",
    });
    const [serviceMessage, setServiceMessage] = useState("");
    const [isSavingService, setIsSavingService] = useState(false);
    const [globalServices, setGlobalServices] = useState([]);

    useEffect(() => {
        const loadGlobalServices = async () => {
            try {
                const res = await adminApi.services();
                if (res?.success && res.data) {
                    setGlobalServices(res.data);
                }
            } catch (err) {
                console.error("Failed to load global services:", err);
            }
        };
        loadGlobalServices();
    }, []);

    const [categoryForm, setCategoryForm] = useState({
        title: "",
        gender: "all",
    });
    const [editingCategory, setEditingCategory] = useState(null);
    const [categoryMessage, setCategoryMessage] = useState("");
    const [isSavingCategory, setIsSavingCategory] = useState(false);
    const [subcategoryForm, setSubcategoryForm] = useState({
        title: "",
        categoryId: "",
    });
    const [editingSubcategory, setEditingSubcategory] = useState(null);
    const [subcategoryMessage, setSubcategoryMessage] = useState("");
    const [isSavingSubcategory, setIsSavingSubcategory] = useState(false);

    const updateServiceField = (field) => (event) => {
        setServiceForm((current) => ({ ...current, [field]: event.target.value }));
    };

    const updateCategoryField = (field) => (event) => {
        setCategoryForm((current) => ({ ...current, [field]: event.target.value }));
    };

    const updateSubcategoryField = (field) => (event) => {
        setSubcategoryForm((current) => ({ ...current, [field]: event.target.value }));
    };

    const resetServiceForm = () => {
        console.log("Function called: resetServiceForm");
        setEditingService(null);
        setServiceForm({ name: "", categoryid: "", subcatid: "", price: "", time: "" });
        setShowServiceForm(false);
    };

    const openCreateService = () => {
        console.log("Function called: openCreateService");
        if (showServiceForm && !editingService) {
            resetServiceForm();
            return;
        }

        setEditingService(null);
        setServiceForm({ name: "", categoryid: "", subcatid: "", price: "", time: "" });
        setServiceMessage("");
        setShowServiceForm(true);
    };

    const openEditService = (service) => {
        console.log("Function called: openEditService");
        if (!service.id) {
            setServiceMessage("This service is missing a backend ID.");
            return;
        }

        const globalService = globalServices.find(s => s.name === service.name && Number(s.categoryid) === Number(service.categoryId) && Number(s.subcatid) === Number(service.subcatId));

        setEditingService(service);
        setServiceForm({
            name: service.name || "",
            categoryid: service.categoryId || "",
            subcatid: service.subcatId || service.subcatid || "",
            price: globalService && globalService.price !== undefined ? String(globalService.price) : (service.price && service.price !== "Not set" ? service.price.replace("$", "") : ""),
            time: globalService && globalService.time ? globalService.time : (service.duration !== "Not set" ? service.duration : ""),
        });
        setServiceMessage("");
        setShowServiceForm(true);
    };

    const saveService = async (event) => {
        console.log("Function called: saveService");
        event.preventDefault();
        setServiceMessage("");
        setIsSavingService(true);

        try {
            console.log("Executing try block...");
            const payload = {
                name: serviceForm.name,
                categoryid: serviceForm.categoryid ? Number(serviceForm.categoryid) : undefined,
                subcatid: serviceForm.subcatid ? Number(serviceForm.subcatid) : undefined,
                price: serviceForm.price ? Number(serviceForm.price) : undefined,
                time: serviceForm.time,
                isactive: true,
                iscomingsoon: false,
            };

            if (editingService) {
                const response = await vendorArtistApi.updateService(editingService.id, payload);
                onServiceUpdated(response.data);
                resetServiceForm();
                setServiceMessage("Service updated successfully.");
            } else {
                const response = await vendorArtistApi.createService(payload);
                onServiceCreated(response.data);
                resetServiceForm();
                setServiceMessage("Service created successfully.");
            }

        } catch (error) {
            console.error("Error caught:", error);
            setServiceMessage(error.message || "Unable to save service.");
        } finally {
            setIsSavingService(false);
        }
    };

    const deleteService = async (service) => {
        console.log("Function called: deleteService");
        if (!service.id || !window.confirm(`Delete ${service.name}?`)) return;
        setServiceMessage("");

        try {
            console.log("Executing try block...");
            await vendorArtistApi.deleteService(service.id);
            onServiceDeleted(service.id);
            setServiceMessage("Service deleted successfully.");
        } catch (error) {
            console.error("Error caught:", error);
            setServiceMessage(error.message || "Unable to delete service.");
        }
    };

    const resetCategoryForm = () => {
        console.log("Function called: resetCategoryForm");
        setEditingCategory(null);
        setCategoryForm({ title: "", gender: "all" });
    };

    const openEditCategory = (category) => {
        console.log("Function called: openEditCategory");
        setEditingCategory(category);
        setCategoryForm({
            title: category.title || category.name || "",
            gender: category.gender || "all",
        });
        setCategoryMessage("");
    };

    const saveCategory = async (event) => {
        console.log("Function called: saveCategory");
        event.preventDefault();
        setCategoryMessage("");
        setIsSavingCategory(true);

        const payload = {
            title: categoryForm.title,
            gender: categoryForm.gender,
            isComingSoon: false,
            isActive: true,
        };

        try {
            console.log("Executing try block...");
            if (editingCategory) {
                const response = await vendorArtistApi.updateCategory(getCategoryId(editingCategory), payload);
                onCategoryUpdated(response.data);
                resetCategoryForm();
                setCategoryMessage("Category updated successfully.");
            } else {
                const response = await vendorArtistApi.createCategory(payload);
                onCategoryCreated(response.data);
                resetCategoryForm();
                setCategoryMessage("Category created successfully.");
            }
        } catch (error) {
            console.error("Error caught:", error);
            setCategoryMessage(error.message || "Unable to save category.");
        } finally {
            setIsSavingCategory(false);
        }
    };

    const deleteCategory = async (category) => {
        console.log("Function called: deleteCategory");
        if (!window.confirm(`Delete ${category.title || category.name || "this category"}?`)) return;

        setCategoryMessage("");
        try {
            console.log("Executing try block...");
            await vendorArtistApi.deleteCategory(getCategoryId(category));
            onCategoryDeleted(getCategoryId(category));
            setCategoryMessage("Category deleted successfully.");
        } catch (error) {
            console.error("Error caught:", error);
            setCategoryMessage(error.message || "Unable to delete category.");
        }
    };

    const resetSubcategoryForm = () => {
        console.log("Function called: resetSubcategoryForm");
        setEditingSubcategory(null);
        setSubcategoryForm({ title: "", categoryId: "" });
    };

    const openEditSubcategory = (subcategory) => {
        console.log("Function called: openEditSubcategory");
        setEditingSubcategory(subcategory);
        setSubcategoryForm({
            title: subcategory.title || subcategory.name || "",
            categoryId: subcategory.categoryid || subcategory.categoryId || "",
        });
        setSubcategoryMessage("");
    };

    const saveSubcategory = async (event) => {
        console.log("Function called: saveSubcategory");
        event.preventDefault();
        setSubcategoryMessage("");

        if (!subcategoryForm.categoryId) {
            setSubcategoryMessage("Choose a parent category first.");
            return;
        }

        setIsSavingSubcategory(true);
        const payload = {
            title: subcategoryForm.title,
            categoryId: Number(subcategoryForm.categoryId),
        };

        try {
            console.log("Executing try block...");
            if (editingSubcategory) {
                const response = await vendorArtistApi.updateSubcategory(getSubcategoryId(editingSubcategory), payload);
                onSubcategoryUpdated(response.data);
                resetSubcategoryForm();
                setSubcategoryMessage("Subcategory updated successfully.");
            } else {
                const response = await vendorArtistApi.createSubcategory(subcategoryForm.categoryId, payload);
                onSubcategoryCreated(response.data);
                resetSubcategoryForm();
                setSubcategoryMessage("Subcategory created successfully.");
            }
        } catch (error) {
            console.error("Error caught:", error);
            setSubcategoryMessage(error.message || "Unable to save subcategory.");
        } finally {
            setIsSavingSubcategory(false);
        }
    };

    const deleteSubcategory = async (subcategory) => {
        console.log("Function called: deleteSubcategory");
        if (!window.confirm(`Delete ${subcategory.title || subcategory.name || "this subcategory"}?`)) return;

        setSubcategoryMessage("");
        try {
            console.log("Executing try block...");
            await vendorArtistApi.deleteSubcategory(getSubcategoryId(subcategory));
            onSubcategoryDeleted(getSubcategoryId(subcategory));
            setSubcategoryMessage("Subcategory deleted successfully.");
        } catch (error) {
            console.error("Error caught:", error);
            setSubcategoryMessage(error.message || "Unable to delete subcategory.");
        }
    };

    const filteredSubcategories = dashboardSubcategories.filter((subcat) => 
        Number(subcat.categoryid || subcat.categoryId) === Number(serviceForm.categoryid)
    );

    const filteredGlobalServices = globalServices.filter((service) => 
        Number(service.categoryid) === Number(serviceForm.categoryid) &&
        Number(service.subcatid || service.subcatId) === Number(serviceForm.subcatid)
    );

    return (
        <div className="space-y-6">
            <Panel
                title="Service management"
                eyebrow="Services"
                action={
                    <button type="button" onClick={openCreateService} className="h-10 rounded-lg bg-blue-600 px-4 text-sm font-bold text-white shadow-lg shadow-blue-100 transition hover:bg-blue-700">
                        {showServiceForm && !editingService ? "Close" : "Add service"}
                    </button>
                }
            >
                {showServiceForm && (
                    <form onSubmit={saveService} className="mb-5 rounded-xl border border-blue-100 bg-blue-50/70 p-4 space-y-3">
                        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Category</label>
                                <select
                                    value={serviceForm.categoryid}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setServiceForm(current => ({ ...current, categoryid: val, subcatid: "", name: "" }));
                                    }}
                                    required
                                    className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                                >
                                    <option value="">Select Category</option>
                                    {dashboardCategories.map((category) => (
                                        <option key={getCategoryId(category)} value={getCategoryId(category)}>
                                            {category.title || category.name || `Category ${getCategoryId(category)}`}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Sub-Category</label>
                                <select
                                    value={serviceForm.subcatid}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setServiceForm(current => ({ ...current, subcatid: val, name: "" }));
                                    }}
                                    required
                                    disabled={!serviceForm.categoryid}
                                    className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-400"
                                >
                                    <option value="">Select Sub-Category</option>
                                    {filteredSubcategories.map((subcat) => (
                                        <option key={getSubcategoryId(subcat)} value={getSubcategoryId(subcat)}>
                                            {subcat.title || subcat.name || `Subcategory ${getSubcategoryId(subcat)}`}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Service</label>
                                <select
                                    value={serviceForm.name}
                                    onChange={(e) => {
                                        const serviceName = e.target.value;
                                        const selectedGlobal = filteredGlobalServices.find(s => s.name === serviceName);
                                        setServiceForm(current => ({
                                            ...current,
                                            name: serviceName,
                                            price: selectedGlobal && selectedGlobal.price ? String(selectedGlobal.price) : current.price,
                                            time: selectedGlobal && selectedGlobal.time ? selectedGlobal.time : current.time
                                        }));
                                    }}
                                    required
                                    disabled={!serviceForm.subcatid}
                                    className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-400"
                                >
                                    <option value="">Select Service</option>
                                    {filteredGlobalServices.map((service) => (
                                        <option key={service.serviceid} value={service.name}>
                                            {service.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Price ($)</label>
                                <input
                                    value={serviceForm.price}
                                    readOnly
                                    type="number"
                                    placeholder="0.00"
                                    className="h-11 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-bold text-slate-500 outline-none cursor-not-allowed"
                                />
                            </div>

                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Duration</label>
                                <input
                                    value={serviceForm.time}
                                    readOnly
                                    placeholder="Duration"
                                    className="h-11 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-bold text-slate-500 outline-none cursor-not-allowed"
                                />
                            </div>
                        </div>

                        {(serviceForm.categoryid || serviceForm.subcatid || serviceForm.name) && (
                            <div className="mt-4 flex flex-col md:flex-row gap-4 p-4 rounded-xl border border-blue-100 bg-white shadow-sm">
                                {(() => {
                                    const catObj = dashboardCategories.find(c => Number(getCategoryId(c)) === Number(serviceForm.categoryid));
                                    const subcatObj = dashboardSubcategories.find(s => Number(getSubcategoryId(s)) === Number(serviceForm.subcatid));
                                    const serviceObj = globalServices.find(s => s.name === serviceForm.name && Number(s.categoryid) === Number(serviceForm.categoryid) && Number(s.subcatid) === Number(serviceForm.subcatid));

                                    return (
                                        <>
                                            {catObj && (
                                                <div className="flex items-center gap-3">
                                                    <img
                                                        src={getImageUrl(catObj.catImage)}
                                                        alt={catObj.title}
                                                        className="h-12 w-12 rounded-lg object-cover border border-slate-100 bg-slate-50"
                                                        onError={(e) => { e.target.src = "https://placehold.co/100x100?text=No+Image"; }}
                                                    />
                                                    <div>
                                                        <span className="text-[10px] uppercase font-bold text-slate-400">Category</span>
                                                        <p className="text-sm font-extrabold text-slate-800">{catObj.title || catObj.name}</p>
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {subcatObj && (
                                                <div className="flex items-center gap-3 md:border-l md:border-slate-100 md:pl-4">
                                                    <img
                                                        src={getImageUrl(subcatObj.subImage || subcatObj.sub_image)}
                                                        alt={subcatObj.title}
                                                        className="h-12 w-12 rounded-lg object-cover border border-slate-100 bg-slate-50"
                                                        onError={(e) => { e.target.src = "https://placehold.co/100x100?text=No+Image"; }}
                                                    />
                                                    <div>
                                                        <span className="text-[10px] uppercase font-bold text-slate-400">Sub-Category</span>
                                                        <p className="text-sm font-extrabold text-slate-800">{subcatObj.title || subcatObj.name}</p>
                                                    </div>
                                                </div>
                                            )}

                                            {serviceObj && (
                                                <div className="flex items-center gap-3 md:border-l md:border-slate-100 md:pl-4 flex-1">
                                                    <img
                                                        src={getImageUrl(serviceObj.service_image)}
                                                        alt={serviceObj.name}
                                                        className="h-12 w-12 rounded-lg object-cover border border-slate-100 bg-slate-50"
                                                        onError={(e) => { e.target.src = "https://placehold.co/100x100?text=No+Image"; }}
                                                    />
                                                    <div>
                                                        <span className="text-[10px] uppercase font-bold text-slate-400">Service</span>
                                                        <p className="text-sm font-extrabold text-slate-800">{serviceObj.name}</p>
                                                        <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full mr-2">
                                                            {serviceForm.price ? `$${Number(serviceForm.price).toFixed(2)}` : "Free"}
                                                        </span>
                                                        <span className="text-xs font-bold text-slate-500 bg-slate-50 px-2 py-0.5 rounded-full">
                                                            {serviceForm.time || "No duration"}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        )}

                        <div className="flex justify-end gap-2 pt-2">
                            <button type="submit" disabled={isSavingService} className="h-11 rounded-xl bg-blue-600 px-6 text-sm font-bold text-white shadow-lg shadow-blue-100 transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70">
                                {isSavingService ? "Saving" : editingService ? "Update" : "Save"}
                            </button>
                            <button type="button" onClick={resetServiceForm} className="h-11 rounded-xl border border-slate-300 bg-white px-6 text-sm font-bold text-slate-700 transition hover:bg-slate-100">
                                Cancel
                            </button>
                        </div>
                    </form>
                )}

                {serviceMessage && (
                    <div className={`mb-5 rounded-xl border px-4 py-3 text-sm font-bold ${isSuccessMessage(serviceMessage) ? "border-emerald-100 bg-emerald-50 text-emerald-700" : "border-red-100 bg-red-50 text-red-700"}`}>
                        {serviceMessage}
                    </div>
                )}

                {serviceRows.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[680px] text-left text-sm">
                            <thead>
                                <tr className="border-b border-slate-100 text-xs uppercase tracking-[0.16em] text-slate-500">
                                    <th className="pb-3 font-bold">Service</th>
                                    <th className="pb-3 font-bold">Category</th>
                                    <th className="pb-3 font-bold">Sub-Category</th>
                                    <th className="pb-3 font-bold">Duration</th>
                                    <th className="pb-3 font-bold">Price</th>
                                    <th className="pb-3 font-bold">Status</th>
                                    <th className="pb-3 text-right font-bold">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {serviceRows.map((service) => {
                                    const subcatObj = dashboardSubcategories.find(sub => Number(getSubcategoryId(sub)) === Number(service.subcatId));
                                    const catObj = dashboardCategories.find(c => Number(getCategoryId(c)) === Number(service.categoryId));
                                    const subcatName = subcatObj?.title || subcatObj?.name || "General";
                                    
                                    const globalService = globalServices.find(s => s.name === service.name && Number(s.categoryid) === Number(service.categoryId) && Number(s.subcatid) === Number(service.subcatId));
                                    const serviceImg = service.serviceImage || service.raw?.service_image || globalService?.service_image;
                                    const durationShow = globalService && globalService.time ? globalService.time : service.duration;
                                    const priceShow = globalService && globalService.price !== undefined ? `$${Number(globalService.price).toFixed(2)}` : service.price;

                                    return (
                                        <tr key={service.id || service.name} className="border-b border-slate-100 last:border-0">
                                            <td className="py-4 font-bold text-slate-900">
                                                <div className="flex items-center gap-3">
                                                    <img
                                                        src={getImageUrl(serviceImg)}
                                                        alt={service.name}
                                                        className="h-10 w-10 rounded-lg object-cover border border-slate-100 bg-slate-50"
                                                        onError={(e) => { e.target.src = "https://placehold.co/100x100?text=No+Image"; }}
                                                    />
                                                    <span>{service.name}</span>
                                                </div>
                                            </td>
                                            <td className="py-4 text-slate-600">
                                                <div className="flex items-center gap-2">
                                                    {catObj && (
                                                        <img
                                                            src={getImageUrl(catObj.catImage)}
                                                            alt={service.category}
                                                            className="h-6 w-6 rounded-md object-cover border border-slate-100 bg-slate-50"
                                                            onError={(e) => { e.target.src = "https://placehold.co/100x100?text=No+Image"; }}
                                                        />
                                                    )}
                                                    <span>{service.category}</span>
                                                </div>
                                            </td>
                                            <td className="py-4 text-slate-600">
                                                <div className="flex items-center gap-2">
                                                    {subcatObj && (
                                                        <img
                                                            src={getImageUrl(subcatObj.subImage || subcatObj.sub_image)}
                                                            alt={subcatName}
                                                            className="h-6 w-6 rounded-md object-cover border border-slate-100 bg-slate-50"
                                                            onError={(e) => { e.target.src = "https://placehold.co/100x100?text=No+Image"; }}
                                                        />
                                                    )}
                                                    <span>{subcatName}</span>
                                                </div>
                                            </td>
                                            <td className="py-4 text-slate-600">{durationShow}</td>
                                            <td className="py-4 font-bold text-slate-900">{priceShow}</td>
                                            <td className="py-4">
                                                <StatusBadge tone={service.status === "Live" ? "green" : "slate"}>{service.status}</StatusBadge>
                                            </td>
                                            <td className="py-4">
                                                <div className="flex justify-end gap-2">
                                                    <button type="button" onClick={() => openEditService(service)} className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-slate-600 transition hover:border-blue-300 hover:text-blue-700" aria-label="Edit service">
                                                        <i className="fa-regular fa-pen-to-square" aria-hidden="true" />
                                                    </button>
                                                    <button type="button" onClick={() => deleteService(service)} className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-slate-600 transition hover:border-blue-300 hover:text-blue-700" aria-label="Delete service">
                                                        <i className="fa-regular fa-trash-can" aria-hidden="true" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <EmptyState
                        title="No services configured"
                        message="Create your first service to start offering appointments to your clients."
                        action={
                            <button type="button" onClick={openCreateService} className="h-10 rounded-lg bg-blue-600 px-4 text-sm font-bold text-white shadow-lg shadow-blue-100 transition hover:bg-blue-700">
                                Add service
                            </button>
                        }
                    />
                )}
            </Panel>

            {/* <Panel title="Portfolio" eyebrow="Profile">
                <div className="grid grid-cols-2 gap-3">
                    {[portfolioOne, portfolioTwo, portfolioThree, specialistImage].map((image, index) => (
                        <div key={image} className="aspect-square overflow-hidden rounded-xl bg-blue-50">
                            <img src={image} alt={`Portfolio ${index + 1}`} className="h-full w-full object-cover" />
                        </div>
                    ))}
                </div>
                <button type="button" className="mt-5 h-10 w-full rounded-lg border border-blue-200 text-sm font-bold text-blue-700 transition hover:bg-blue-50">
                    Upload portfolio
                </button>
            </Panel> */}

        </div>
    );
}

// eslint-disable-next-line no-unused-vars
export function ContentPanel({ dashboardBlogs, dashboardSponsors, onBlogCreated, onBlogUpdated, onBlogDeleted, onSponsorCreated, onSponsorUpdated, onSponsorDeleted }) {
    console.log("Function called: ContentPanel");
    const blogRows = dashboardBlogs.map(normalizeBlog);
    const sponsorRows = dashboardSponsors.map(normalizeSponsor);
    const [showBlogForm, setShowBlogForm] = useState(false);
    const [editingBlog, setEditingBlog] = useState(null);
    const [blogForm, setBlogForm] = useState({
        title: "",
        description: "",
        image: "",
        showIn: "both",
        status: "active",
    });
    const [blogMessage, setBlogMessage] = useState("");
    const [isSavingBlog, setIsSavingBlog] = useState(false);
    const [showSponsorForm, setShowSponsorForm] = useState(false);
    const [editingSponsor, setEditingSponsor] = useState(null);
    const [sponsorForm, setSponsorForm] = useState({
        name: "",
        order: "",
        image: "",
        sponserDetails: "",
        status: "active",
    });
    const [sponsorMessage, setSponsorMessage] = useState("");
    const [isSavingSponsor, setIsSavingSponsor] = useState(false);

    const updateBlogField = (field) => (event) => {
        setBlogForm((current) => ({ ...current, [field]: event.target.value }));
    };

    const updateSponsorField = (field) => (event) => {
        setSponsorForm((current) => ({ ...current, [field]: event.target.value }));
    };

    const resetBlogForm = () => {
        console.log("Function called: resetBlogForm");
        setEditingBlog(null);
        setBlogForm({ title: "", description: "", image: "", showIn: "both", status: "active" });
        setShowBlogForm(false);
    };

    const openCreateBlog = () => {
        console.log("Function called: openCreateBlog");
        if (showBlogForm && !editingBlog) {
            resetBlogForm();
            return;
        }

        setEditingBlog(null);
        setBlogForm({ title: "", description: "", image: "", showIn: "both", status: "active" });
        setBlogMessage("");
        setShowBlogForm(true);
    };

    const openEditBlog = (blog) => {
        console.log("Function called: openEditBlog");
        setEditingBlog(blog);
        setBlogForm({
            title: blog.title || "",
            description: blog.description || "",
            image: blog.image || "",
            showIn: blog.showIn || "both",
            status: blog.status || "active",
        });
        setBlogMessage("");
        setShowBlogForm(true);
    };

    const saveBlog = async (event) => {
        console.log("Function called: saveBlog");
        event.preventDefault();
        setBlogMessage("");
        setIsSavingBlog(true);

        const payload = {
            title: blogForm.title,
            description: blogForm.description,
            image: blogForm.image || null,
            showIn: blogForm.showIn,
            status: blogForm.status,
        };

        try {
            // Blog management is not part of the vendor platform
            setBlogMessage("Blog management is not available for vendor accounts.");
        } catch (error) {
            console.error("Error caught:", error);
            setBlogMessage(error.message || "Unable to save blog.");
        } finally {
            setIsSavingBlog(false);
        }
    };

    const deleteBlog = async (blog) => {
        console.log("Function called: deleteBlog");
        if (!window.confirm(`Delete ${blog.title}?`)) return;

        setBlogMessage("");
        try {
            // Blog management is not part of the vendor platform
            setBlogMessage("Blog management is not available for vendor accounts.");
        } catch (error) {
            console.error("Error caught:", error);
            setBlogMessage(error.message || "Unable to delete blog.");
        }
    };

    const resetSponsorForm = () => {
        console.log("Function called: resetSponsorForm");
        setEditingSponsor(null);
        setSponsorForm({ name: "", order: "", image: "", sponserDetails: "", status: "active" });
        setShowSponsorForm(false);
    };

    const openCreateSponsor = () => {
        console.log("Function called: openCreateSponsor");
        if (showSponsorForm && !editingSponsor) {
            resetSponsorForm();
            return;
        }

        setEditingSponsor(null);
        setSponsorForm({ name: "", order: "", image: "", sponserDetails: "", status: "active" });
        setSponsorMessage("");
        setShowSponsorForm(true);
    };

    const openEditSponsor = (sponsor) => {
        console.log("Function called: openEditSponsor");
        setEditingSponsor(sponsor);
        setSponsorForm({
            name: sponsor.name || "",
            order: sponsor.order || "",
            image: sponsor.image || "",
            sponserDetails: sponsor.details || "",
            status: sponsor.status || "active",
        });
        setSponsorMessage("");
        setShowSponsorForm(true);
    };

    const saveSponsor = async (event) => {
        console.log("Function called: saveSponsor");
        event.preventDefault();
        setSponsorMessage("");
        setIsSavingSponsor(true);

        const payload = {
            name: sponsorForm.name,
            order: sponsorForm.order ? Number(sponsorForm.order) : undefined,
            image: sponsorForm.image || null,
            sponserDetails: sponsorForm.sponserDetails,
            status: sponsorForm.status,
        };

        try {
            // Sponsor management is not part of the vendor platform
            setSponsorMessage("Sponsor management is not available for vendor accounts.");
        } catch (error) {
            console.error("Error caught:", error);
            setSponsorMessage(error.message || "Unable to save sponsor.");
        } finally {
            setIsSavingSponsor(false);
        }
    };

    const deleteSponsor = async (sponsor) => {
        console.log("Function called: deleteSponsor");
        if (!window.confirm(`Delete ${sponsor.name}?`)) return;

        setSponsorMessage("");
        try {
            // Sponsor management is not part of the vendor platform
            setSponsorMessage("Sponsor management is not available for vendor accounts.");
        } catch (error) {
            console.error("Error caught:", error);
            setSponsorMessage(error.message || "Unable to delete sponsor.");
        }
    };

    return (
        <div className="space-y-6">
            <Panel
                title="Blog management"
                eyebrow="Content"
                action={
                    <button type="button" onClick={openCreateBlog} className="h-10 rounded-lg bg-blue-600 px-4 text-sm font-bold text-white shadow-lg shadow-blue-100 transition hover:bg-blue-700">
                        {showBlogForm && !editingBlog ? "Close" : "Add blog"}
                    </button>
                }
            >
                {showBlogForm && (
                    <form onSubmit={saveBlog} className="mb-5 grid gap-3 rounded-xl border border-blue-100 bg-blue-50/70 p-4">
                        <div className="grid gap-3 lg:grid-cols-[1fr_180px_160px]">
                            <input
                                value={blogForm.title}
                                onChange={updateBlogField("title")}
                                required
                                placeholder="Blog title"
                                className="h-11 rounded-xl border border-slate-200 px-3 text-sm font-bold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                            />
                            <select
                                value={blogForm.showIn}
                                onChange={updateBlogField("showIn")}
                                className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                            >
                                <option value="both">Website + mobile</option>
                                <option value="website">Website</option>
                                <option value="mobile">Mobile</option>
                            </select>
                            <select
                                value={blogForm.status}
                                onChange={updateBlogField("status")}
                                className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                            >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                        <input
                            value={blogForm.image}
                            onChange={updateBlogField("image")}
                            placeholder="Image URL"
                            className="h-11 rounded-xl border border-slate-200 px-3 text-sm font-bold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                        />
                        <textarea
                            value={blogForm.description}
                            onChange={updateBlogField("description")}
                            required
                            placeholder="Blog description"
                            className="min-h-[140px] rounded-xl border border-slate-200 px-3 py-3 text-sm font-semibold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                        />
                        <div className="flex flex-wrap gap-3">
                            <button type="submit" disabled={isSavingBlog} className="h-11 rounded-xl bg-blue-600 px-4 text-sm font-bold text-white shadow-lg shadow-blue-100 transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70">
                                {isSavingBlog ? "Saving" : editingBlog ? "Update blog" : "Save blog"}
                            </button>
                            <button type="button" onClick={resetBlogForm} className="h-11 rounded-xl border border-slate-300 bg-white px-4 text-sm font-bold text-slate-700 transition hover:bg-slate-100">
                                Cancel
                            </button>
                        </div>
                    </form>
                )}

                {blogMessage && (
                    <div className={`mb-5 rounded-xl border px-4 py-3 text-sm font-bold ${isSuccessMessage(blogMessage) ? "border-emerald-100 bg-emerald-50 text-emerald-700" : "border-red-100 bg-red-50 text-red-700"}`}>
                        {blogMessage}
                    </div>
                )}

                {blogRows.length > 0 ? (
                    <div className="grid gap-4 lg:grid-cols-2">
                        {blogRows.map((blog) => (
                            <article key={blog.id} className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                        <h3 className="text-lg font-extrabold text-[#191E49]">{blog.title}</h3>
                                        <p className="mt-1 text-xs font-bold uppercase tracking-[0.16em] text-slate-500">{blog.showIn}</p>
                                    </div>
                                    <StatusBadge tone={blog.status === "active" ? "green" : "slate"}>{blog.status}</StatusBadge>
                                </div>
                                <p className="mt-3 line-clamp-3 text-sm leading-6 text-slate-600">{blog.description.replace(/<[^>]*>/g, " ")}</p>
                                <div className="mt-4 flex justify-end gap-2">
                                    <button type="button" onClick={() => openEditBlog(blog)} className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-slate-600 transition hover:border-blue-300 hover:text-blue-700" aria-label="Edit blog">
                                        <i className="fa-regular fa-pen-to-square" aria-hidden="true" />
                                    </button>
                                    <button type="button" onClick={() => deleteBlog(blog)} className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-slate-600 transition hover:border-blue-300 hover:text-blue-700" aria-label="Delete blog">
                                        <i className="fa-regular fa-trash-can" aria-hidden="true" />
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>
                ) : (
                    <EmptyState
                        title="No blogs published"
                        message="Create and publish blog posts to share updates with your clients."
                        action={
                            <button type="button" onClick={openCreateBlog} className="h-10 rounded-lg bg-blue-600 px-4 text-sm font-bold text-white shadow-lg shadow-blue-100 transition hover:bg-blue-700">
                                Add blog
                            </button>
                        }
                    />
                )}
            </Panel>

            <Panel
                title="Sponsor management"
                eyebrow="Content"
                action={
                    <button type="button" onClick={openCreateSponsor} className="h-10 rounded-lg bg-blue-600 px-4 text-sm font-bold text-white shadow-lg shadow-blue-100 transition hover:bg-blue-700">
                        {showSponsorForm && !editingSponsor ? "Close" : "Add sponsor"}
                    </button>
                }
            >
                {showSponsorForm && (
                    <form onSubmit={saveSponsor} className="mb-5 grid gap-3 rounded-xl border border-blue-100 bg-blue-50/70 p-4">
                        <div className="grid gap-3 lg:grid-cols-[1fr_130px_160px]">
                            <input
                                value={sponsorForm.name}
                                onChange={updateSponsorField("name")}
                                required
                                placeholder="Sponsor name"
                                className="h-11 rounded-xl border border-slate-200 px-3 text-sm font-bold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                            />
                            <input
                                value={sponsorForm.order}
                                onChange={updateSponsorField("order")}
                                type="number"
                                min="0"
                                placeholder="Order"
                                className="h-11 rounded-xl border border-slate-200 px-3 text-sm font-bold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                            />
                            <select
                                value={sponsorForm.status}
                                onChange={updateSponsorField("status")}
                                className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                            >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                        <input
                            value={sponsorForm.image}
                            onChange={updateSponsorField("image")}
                            placeholder="Image URL"
                            className="h-11 rounded-xl border border-slate-200 px-3 text-sm font-bold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                        />
                        <textarea
                            value={sponsorForm.sponserDetails}
                            onChange={updateSponsorField("sponserDetails")}
                            placeholder="Sponsor details"
                            className="min-h-[110px] rounded-xl border border-slate-200 px-3 py-3 text-sm font-semibold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                        />
                        <div className="flex flex-wrap gap-3">
                            <button type="submit" disabled={isSavingSponsor} className="h-11 rounded-xl bg-blue-600 px-4 text-sm font-bold text-white shadow-lg shadow-blue-100 transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70">
                                {isSavingSponsor ? "Saving" : editingSponsor ? "Update sponsor" : "Save sponsor"}
                            </button>
                            <button type="button" onClick={resetSponsorForm} className="h-11 rounded-xl border border-slate-300 bg-white px-4 text-sm font-bold text-slate-700 transition hover:bg-slate-100">
                                Cancel
                            </button>
                        </div>
                    </form>
                )}

                {sponsorMessage && (
                    <div className={`mb-5 rounded-xl border px-4 py-3 text-sm font-bold ${isSuccessMessage(sponsorMessage) ? "border-emerald-100 bg-emerald-50 text-emerald-700" : "border-red-100 bg-red-50 text-red-700"}`}>
                        {sponsorMessage}
                    </div>
                )}

                {sponsorRows.length > 0 ? (
                    <div className="grid gap-4 lg:grid-cols-2">
                        {sponsorRows.map((sponsor) => (
                            <article key={sponsor.id} className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                        <h3 className="text-lg font-extrabold text-[#191E49]">{sponsor.name}</h3>
                                        <p className="mt-1 text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Order {sponsor.order || "not set"}</p>
                                    </div>
                                    <StatusBadge tone={sponsor.status === "active" ? "green" : "slate"}>{sponsor.status}</StatusBadge>
                                </div>
                                <p className="mt-3 line-clamp-3 text-sm leading-6 text-slate-600">{sponsor.details || "No sponsor details added."}</p>
                                <div className="mt-4 flex justify-end gap-2">
                                    <button type="button" onClick={() => openEditSponsor(sponsor)} className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-slate-600 transition hover:border-blue-300 hover:text-blue-700" aria-label="Edit sponsor">
                                        <i className="fa-regular fa-pen-to-square" aria-hidden="true" />
                                    </button>
                                    <button type="button" onClick={() => deleteSponsor(sponsor)} className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-slate-600 transition hover:border-blue-300 hover:text-blue-700" aria-label="Delete sponsor">
                                        <i className="fa-regular fa-trash-can" aria-hidden="true" />
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>
                ) : (
                    <EmptyState
                        title="No sponsors configured"
                        message="Add partner logos or sponsor details to display on your booking page."
                        action={
                            <button type="button" onClick={openCreateSponsor} className="h-10 rounded-lg bg-blue-600 px-4 text-sm font-bold text-white shadow-lg shadow-blue-100 transition hover:bg-blue-700">
                                Add sponsor
                            </button>
                        }
                    />
                )}
            </Panel>
        </div>
    );
}

// eslint-disable-next-line no-unused-vars
export function StudiosPanel({ dashboardStudios }) {
    console.log("Function called: StudiosPanel");
    const studioRows = dashboardStudios.map(normalizeRentalStudio);
    const [searchForm, setSearchForm] = useState({ q: "", pincode: "", capacity: "" });
    const [searchRows, setSearchRows] = useState(null);
    const [studioDetails, setStudioDetails] = useState({});
    const [studioMessage, setStudioMessage] = useState("");
    const [isSearchingStudios, setIsSearchingStudios] = useState(false);
    const [loadingStudioId, setLoadingStudioId] = useState(null);
    const rows = searchRows ? searchRows.map(normalizeRentalStudio) : studioRows;

    const updateStudioSearchField = (field) => (event) => {
        setSearchForm((current) => ({ ...current, [field]: event.target.value }));
    };

    const searchStudios = async () => {
        console.log("Function called: searchStudios");
        const params = new URLSearchParams();
        Object.entries(searchForm).forEach(([key, value]) => {
            if (value) params.set(key, value);
        });

        setIsSearchingStudios(true);
        setStudioMessage("");
        try {
            console.log("Executing try block...");
            const response = await publicApi.searchRentalStudios(params.toString());
            setSearchRows(response.data || response.studios || []);
            setStudioMessage("Rental studio search loaded from backend.");
        } catch (error) {
            console.error("Error caught:", error);
            setStudioMessage(error.message || "Unable to search rental studios.");
        } finally {
            setIsSearchingStudios(false);
        }
    };

    const loadStudioDetails = async (studio) => {
        console.log("Function called: loadStudioDetails");
        if (!studio.id) {
            setStudioMessage("This studio is missing a backend ID.");
            return;
        }

        setLoadingStudioId(studio.id);
        setStudioMessage("");
        try {
            console.log("Executing try block...");
            const response = await publicApi.rentalStudioDetails(studio.id);
            setStudioDetails((current) => ({ ...current, [studio.id]: response.data || response.studio || response }));
        } catch (error) {
            console.error("Error caught:", error);
            setStudioMessage(error.message || "Unable to load rental studio details.");
        } finally {
            setLoadingStudioId(null);
        }
    };

    return (
        <Panel title="Rental studios" eyebrow="Studios">
            <div className="mb-5 grid gap-3 rounded-xl border border-blue-100 bg-blue-50/70 p-4 md:grid-cols-[1fr_140px_140px_auto_auto]">
                <input value={searchForm.q} onChange={updateStudioSearchField("q")} placeholder="Search" className="h-10 rounded-lg border border-slate-200 px-3 text-sm font-semibold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100" />
                <input value={searchForm.pincode} onChange={updateStudioSearchField("pincode")} placeholder="Pincode" className="h-10 rounded-lg border border-slate-200 px-3 text-sm font-semibold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100" />
                <input value={searchForm.capacity} onChange={updateStudioSearchField("capacity")} type="number" min="1" placeholder="Capacity" className="h-10 rounded-lg border border-slate-200 px-3 text-sm font-semibold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100" />
                <button type="button" onClick={searchStudios} disabled={isSearchingStudios} className="h-10 rounded-lg bg-blue-600 px-4 text-sm font-bold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70">
                    {isSearchingStudios ? "Searching" : "Search"}
                </button>
                <button type="button" onClick={() => { setSearchRows(null); setSearchForm({ q: "", pincode: "", capacity: "" }); setStudioMessage(""); }} className="h-10 rounded-lg border border-slate-300 bg-white px-4 text-sm font-bold text-slate-700 transition hover:bg-slate-100">
                    Reset
                </button>
            </div>
            {studioMessage && (
                <div className={`mb-5 rounded-xl border px-4 py-3 text-sm font-bold ${isSuccessMessage(studioMessage) ? "border-emerald-100 bg-emerald-50 text-emerald-700" : "border-red-100 bg-red-50 text-red-700"}`}>
                    {studioMessage}
                </div>
            )}
            {rows.length > 0 ? (
                <div className="grid gap-4 lg:grid-cols-2">
                    {rows.map((studio) => (
                        <article key={studio.id || studio.name} className="overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
                            {studio.image && (
                                <img src={studio.image} alt={studio.name} className="h-44 w-full object-cover" />
                            )}
                            <div className="p-4">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                        <h3 className="text-lg font-extrabold text-[#191E49]">{studio.name}</h3>
                                        <p className="mt-1 text-sm font-semibold text-slate-500">
                                            {[studio.city, studio.state, studio.pincode].filter(Boolean).join(", ") || "Location not set"}
                                        </p>
                                    </div>
                                    <StatusBadge tone={studio.status === "Available" ? "green" : "slate"}>{studio.status}</StatusBadge>
                                </div>
                                <p className="mt-3 line-clamp-3 text-sm leading-6 text-slate-600">{studio.description}</p>
                                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                                    <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Capacity</p>
                                        <p className="mt-1 font-extrabold text-slate-900">{studio.capacity}</p>
                                    </div>
                                    <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Price</p>
                                        <p className="mt-1 font-extrabold text-slate-900">{studio.price}</p>
                                    </div>
                                </div>
                                {studioDetails[studio.id] && (
                                    <pre className="mt-4 max-h-52 overflow-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-xs font-semibold leading-5 text-slate-700">
                                        {JSON.stringify(studioDetails[studio.id], null, 2)}
                                    </pre>
                                )}
                                <button type="button" onClick={() => loadStudioDetails(studio)} disabled={loadingStudioId === studio.id} className="mt-4 h-10 rounded-lg border border-blue-200 px-4 text-sm font-bold text-blue-700 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-70">
                                    {loadingStudioId === studio.id ? "Loading details" : "View details"}
                                </button>
                            </div>
                        </article>
                    ))}
                </div>
            ) : (
                <EmptyState
                    title="No rental studios found"
                    message="Rental studios and creative work spaces will be shown here."
                />
            )}
        </Panel>
    );
}

// eslint-disable-next-line no-unused-vars
export function PageantsPanel({ dashboardPageants }) {
    console.log("Function called: PageantsPanel");
    const pageantRows = dashboardPageants.map(normalizePageant);
    const [pageantDetails, setPageantDetails] = useState({});
    const [pageantMessage, setPageantMessage] = useState("");
    const [loadingPageantId, setLoadingPageantId] = useState(null);

    const loadPageantDetails = async (pageant) => {
        console.log("Function called: loadPageantDetails");
        if (!pageant.id) {
            setPageantMessage("This pageant is missing a backend ID.");
            return;
        }

        setLoadingPageantId(pageant.id);
        setPageantMessage("");
        try {
            console.log("Executing try block...");
            const response = await publicApi.pageantDetails(pageant.id);
            setPageantDetails((current) => ({ ...current, [pageant.id]: response.data || response.pageant || response }));
        } catch (error) {
            console.error("Error caught:", error);
            setPageantMessage(error.message || "Unable to load pageant details.");
        } finally {
            setLoadingPageantId(null);
        }
    };

    return (
        <Panel title="Pageants" eyebrow="Events">
            {pageantMessage && (
                <div className={`mb-5 rounded-xl border px-4 py-3 text-sm font-bold ${isSuccessMessage(pageantMessage) ? "border-emerald-100 bg-emerald-50 text-emerald-700" : "border-red-100 bg-red-50 text-red-700"}`}>
                    {pageantMessage}
                </div>
            )}
            {pageantRows.length > 0 ? (
                <div className="grid gap-4 lg:grid-cols-2">
                    {pageantRows.map((pageant) => (
                        <article key={pageant.id || pageant.title} className="overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
                            {pageant.image && <img src={pageant.image} alt={pageant.title} className="h-44 w-full object-cover" />}
                            <div className="p-4">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                        <h3 className="text-lg font-extrabold text-[#191E49]">{pageant.title}</h3>
                                        <p className="mt-1 text-sm font-semibold text-slate-500">
                                            {[pageant.city, pageant.state].filter(Boolean).join(", ") || pageant.date || "Location not set"}
                                        </p>
                                    </div>
                                    <StatusBadge tone={pageant.status === "active" ? "green" : "slate"}>{pageant.status}</StatusBadge>
                                </div>
                                <p className="mt-3 line-clamp-3 text-sm leading-6 text-slate-600">{pageant.description}</p>
                                {pageantDetails[pageant.id] && (
                                    <pre className="mt-4 max-h-52 overflow-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-xs font-semibold leading-5 text-slate-700">
                                        {JSON.stringify(pageantDetails[pageant.id], null, 2)}
                                    </pre>
                                )}
                                <button type="button" onClick={() => loadPageantDetails(pageant)} disabled={loadingPageantId === pageant.id} className="mt-4 h-10 rounded-lg border border-blue-200 px-4 text-sm font-bold text-blue-700 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-70">
                                    {loadingPageantId === pageant.id ? "Loading details" : "View details"}
                                </button>
                            </div>
                        </article>
                    ))}
                </div>
            ) : (
                <EmptyState title="No pageants scheduled" message="Upcoming events and pageant details will appear here." />
            )}
        </Panel>
    );
}